/*
 * Reading typography for a dossier: a measured column, generous vertical
 * rhythm, and headings that step down clearly. The page's own h1 stays the
 * product name, so the dossier's first heading is drawn one size smaller
 * than the page title without changing what it means.
 */
.markdown {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);

  max-width: var(--measure-text);

  font-size: var(--size-300);
  line-height: var(--leading-normal);
}

.markdown p {
  max-width: none;
}

.h1 {
  margin-block-start: var(--space-s);

  font-size: var(--size-500);
}

.h2 {
  margin-block-start: var(--space-l);
  padding-block-end: var(--space-2xs);

  font-size: var(--size-400);

  border-block-end: 1px solid var(--border);
}

.h3 {
  margin-block-start: var(--space-m);

  font-size: var(--size-300);
  font-weight: var(--weight-strong);
}

.h4,
.h5,
.h6 {
  margin-block-start: var(--space-s);

  font-size: var(--size-300);
  font-weight: var(--weight-medium);

  color: var(--text-muted);
}

.markdown ul,
.markdown ol {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);

  margin: 0;
  padding-inline-start: var(--space-l);
}

.markdown li > ul,
.markdown li > ol {
  margin-block-start: var(--space-2xs);
}

.markdown li::marker {
  color: var(--text-muted);
}

.markdown blockquote {
  margin: 0;
  padding-inline-start: var(--space-m);

  color: var(--text-muted);

  border-inline-start: 3px solid var(--border-strong);
}

.markdown hr {
  margin-block: var(--space-s);

  border: none;
  border-block-start: 1px solid var(--border);
}

.markdown strong {
  font-weight: var(--weight-strong);

  color: var(--text);
}

/* Front matter as a fact sheet: two quiet columns ahead of the text. */
.facts {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: var(--space-2xs) var(--space-m);

  margin: 0;
  padding: var(--space-s) var(--space-m);

  font-size: var(--size-200);

  background-color: var(--surface-sunken);

  border-radius: var(--radius-s);
}

.fact {
  display: contents;
}

.facts dt {
  font-weight: var(--weight-medium);
  text-transform: capitalize;

  color: var(--text-muted);
}

.facts dd {
  margin: 0;
}

.scroll {
  overflow-x: auto;

  border: 1px solid var(--border);
  border-radius: var(--radius-s);
}

.table {
  width: 100%;

  border-collapse: collapse;

  font-size: var(--size-200);
}

.table th,
.table td {
  padding: var(--space-xs) var(--space-s);

  text-align: start;
  vertical-align: top;

  border-block-end: 1px solid var(--border);
}

.table tr:last-child > * {
  border-block-end: none;
}

.table thead th {
  font-weight: var(--weight-medium);

  color: var(--text-muted);
  background-color: var(--surface-sunken);
}

/* A row label in a key/value table reads as a label, not as a second column of text. */
.table tbody th {
  width: 1%;

  font-weight: var(--weight-medium);
  white-space: nowrap;

  color: var(--text-muted);
}

.code {
  margin: 0;
  padding: var(--space-s) var(--space-m);

  overflow-x: auto;

  font-family: var(--font-mono);
  font-size: var(--size-200);

  background-color: var(--field);

  border-radius: var(--radius-s);
}

.inlineCode {
  padding: 0 var(--space-3xs);

  font-family: var(--font-mono);
  font-size: 0.9em;

  background-color: var(--surface-sunken);

  border-radius: var(--radius-xs);
}
